import Paper from '@mui/material/Paper';
import { SxProps, Theme } from '@mui/material/styles';
import {
  DataGrid,
  DataGridProps,
  GridRowClassNameParams,
  GridRowParams,
  GridValidRowModel,
} from '@mui/x-data-grid';
import { useSyncExternalStore } from 'react';

import { dashboardScrollContainerSx } from 'src/common/components/dashboardPageLayout';
import { dashboardTablePaperSx } from 'src/common/components/table/dashboardTableLayout';

import {
  DASHBOARD_GRID_ROW_HEIGHT,
  DashboardGridChrome,
  shouldUseCompactDashboardGridHeight,
  useDashboardGridScrollHeight,
} from './useDashboardGridHeight';

function useDashboardGridFitsContent(rowCount: number, chrome: DashboardGridChrome): boolean {
  return useSyncExternalStore(
    onStoreChange => {
      window.addEventListener('resize', onStoreChange);
      return () => window.removeEventListener('resize', onStoreChange);
    },
    () => shouldUseCompactDashboardGridHeight(rowCount, chrome),
    () => true,
  );
}

type DashboardDataGridLayout = 'virtualized' | 'autoHeight';

type Props<R extends GridValidRowModel & { id: string }> = Omit<
  DataGridProps<R>,
  'rowHeight' | 'hideFooter' | 'disableRowSelectionOnClick'
> & {
  'aria-label': string;
  embedded?: boolean;
  chrome?: DashboardGridChrome;
  layout?: DashboardDataGridLayout;
  paperSx?: SxProps<Theme>;
  onRowClick?: (params: GridRowParams<R>) => void;
  getRowClassName?: (params: GridRowClassNameParams<R>) => string;
};

function DashboardDataGrid<R extends GridValidRowModel & { id: string }>({
  embedded = false,
  chrome = 'standard',
  layout = 'virtualized',
  paperSx,
  onRowClick,
  getRowClassName,
  sx,
  ...dataGridProps
}: Props<R>) {
  const chromeKey: DashboardGridChrome = embedded ? 'embedded' : chrome;
  const rowCount = dataGridProps.rows?.length ?? 0;
  const fitsContent = useDashboardGridFitsContent(rowCount, chromeKey);
  const height = useDashboardGridScrollHeight(chromeKey);
  const isVirtualized = layout === 'virtualized' && !fitsContent;

  const paperStyles: SxProps<Theme> = {
    ...dashboardTablePaperSx,
    ...(embedded ? { mb: 0, mt: 0 } : {}),
    display: 'flex',
    flexDirection: 'column',
    ...(isVirtualized ? { height, flex: 1, minHeight: 0 } : {}),
    ...(paperSx as object),
  };

  return (
    <Paper sx={paperStyles}>
      <DataGrid<R>
        {...dataGridProps}
        getRowId={dataGridProps.getRowId ?? (row => row.id)}
        {...(dataGridProps.getRowHeight ? {} : { rowHeight: DASHBOARD_GRID_ROW_HEIGHT })}
        hideFooter
        disableRowSelectionOnClick
        disableVirtualization={!isVirtualized}
        autoHeight={!isVirtualized}
        onRowClick={onRowClick}
        getRowClassName={getRowClassName}
        sx={{
          border: 0,
          ...(isVirtualized ? { flex: 1, minHeight: 0 } : {}),
          ...(isVirtualized
            ? {
                '& .MuiDataGrid-virtualScroller': dashboardScrollContainerSx,
              }
            : {}),
          '& .MuiDataGrid-row:last-child .MuiDataGrid-cell': {
            borderBottom: 0,
          },
          ...sx,
        }}
      />
    </Paper>
  );
}

export default DashboardDataGrid;
